import { useContractRead } from 'wagmi'
import { Abi } from 'viem'
import { CONFIG, ADDRESSES } from '../config'

interface Property {
  key: string
  valueType: number
  stringValue: string
  uintValue: bigint
  boolValue: boolean
  bytesValue: string
}

interface ContractReadResult<T> {
  data?: T
  isLoading: boolean
  error?: Error
}

/**
 * Smart hook for accessing properties that handles both TrinityProperties and TrinityEntity contracts
 */
export function useGetProperty(
  assetAddress: `0x${string}` | undefined,
  key: string,
  enabled: boolean = true
): Property | undefined {
  // First get the properties contract address
  const { data: propertiesAddress } = useContractRead({
    address: assetAddress,
    abi: CONFIG.TRINITY_PRODUCT_ABI.abi as Abi,
    functionName: 'properties',
    enabled: enabled && !!assetAddress,
    watch: true,
  })

  // Check if the properties address is the same as the asset address (indicating it's a TrinityEntity)
  const isEntityContract = propertiesAddress === assetAddress

  // Use the appropriate function based on the contract type
  const { data: property } = useContractRead({
    address: isEntityContract ? assetAddress : (propertiesAddress as `0x${string}`),
    abi: isEntityContract ? CONFIG.TRINITY_ENTITY_ABI.abi as Abi : CONFIG.TRINITY_PROPERTIES_ABI.abi as Abi,
    functionName: 'getProperty',
    args: isEntityContract ? [key] : [assetAddress, key],
    enabled: enabled && !!assetAddress && (isEntityContract || !!propertiesAddress),
    watch: true,
  }) as { data: Property }

  return property
}

/**
 * Hook for getting properties from TrinityEntity contract (which has a different getProperty signature)
 */
export function useGetEntityProperty(
  entityAddress: `0x${string}` | undefined,
  key: string,
  enabled: boolean = true
): Property | undefined {
  const { data: property } = useContractRead({
    address: entityAddress,
    abi: CONFIG.TRINITY_ENTITY_ABI.abi as Abi,
    functionName: 'getProperty',
    args: [key],
    enabled: enabled && !!entityAddress,
    watch: true,
  }) as { data: Property }

  return property
}

/**
 * Hook for accessing product settings and properties
 */
export function useProductSettings(productAddress: `0x${string}` | undefined, enabled: boolean = true) {
  // Get the properties contract address
  const { data: propertiesAddress } = useContractRead({
    address: productAddress,
    abi: CONFIG.TRINITY_PRODUCT_ABI.abi as Abi,
    functionName: 'properties',
    enabled: enabled && !!productAddress,
    watch: true,
  })

  // Get basic product info
  const name = useContractRead({
    address: productAddress,
    abi: CONFIG.TRINITY_PRODUCT_ABI.abi as Abi,
    functionName: 'name',
    enabled: enabled && !!productAddress,
  }) as ContractReadResult<string>

  const description = useContractRead({
    address: productAddress,
    abi: CONFIG.TRINITY_PRODUCT_ABI.abi as Abi,
    functionName: 'description',
    enabled: enabled && !!productAddress,
  }) as ContractReadResult<string>

  // Get product properties
  const reclaimFee = useContractRead({
    address: propertiesAddress as `0x${string}`,
    abi: CONFIG.TRINITY_PROPERTIES_ABI.abi as Abi,
    functionName: 'getProperty',
    args: [productAddress as `0x${string}`, 'reclaimFee'],
    enabled: enabled && !!productAddress && !!propertiesAddress,
    select: (data: any) => data?.uintValue || 0n
  }) as ContractReadResult<bigint>

  const redemptionFee = useContractRead({
    address: propertiesAddress as `0x${string}`,
    abi: CONFIG.TRINITY_PROPERTIES_ABI.abi as Abi,
    functionName: 'getProperty',
    args: [productAddress as `0x${string}`, 'redemptionFee'],
    enabled: enabled && !!productAddress && !!propertiesAddress,
    select: (data: any) => data?.uintValue || 0n
  }) as ContractReadResult<bigint>

  const latePenalty = useContractRead({
    address: propertiesAddress as `0x${string}`,
    abi: CONFIG.TRINITY_PROPERTIES_ABI.abi as Abi,
    functionName: 'getProperty',
    args: [productAddress as `0x${string}`, 'lateRedemptionPenalty'],
    enabled: enabled && !!productAddress && !!propertiesAddress,
    select: (data: any) => data?.uintValue || 0n
  }) as ContractReadResult<bigint>

  const trinityFee = useContractRead({
    address: productAddress,
    abi: CONFIG.TRINITY_PRODUCT_ABI.abi as Abi,
    functionName: 'trinityFee',
    enabled: enabled && !!productAddress
  }) as ContractReadResult<bigint>

  const trinityServiceFee = useContractRead({
    address: ADDRESSES.TRINITY_MASTER,
    abi: CONFIG.TRINITY_ABI.abi as Abi,
    functionName: 'trinityMasterServiceFee',
    watch: true,
    enabled: true
  }) as ContractReadResult<bigint>

  const isLoading = [
    name,
    description,
    reclaimFee,
    redemptionFee,
    latePenalty,
    trinityFee,
    trinityServiceFee
  ].some(read => read.isLoading)

  const errors = [
    name,
    description,
    reclaimFee,
    redemptionFee,
    latePenalty,
    trinityFee,
    trinityServiceFee
  ].map(read => read.error).filter(Boolean)

  return {
    name: name.data,
    description: description.data,
    reclaimFee: reclaimFee.data,
    redemptionFee: redemptionFee.data,
    latePenalty: latePenalty.data,
    trinityFee: trinityFee.data,
    trinityServiceFee: trinityServiceFee.data,
    isLoading,
    errors
  }
}

// Convenience hooks for specific property types
export function useGetRedemptionFee(assetAddress: `0x${string}` | undefined, enabled: boolean = true): bigint {
  const property = useGetProperty(assetAddress, 'redemptionFee', enabled)
  return property?.uintValue ?? 0n
}

export function useGetReclaimFee(assetAddress: `0x${string}` | undefined, enabled: boolean = true): bigint {
  const property = useGetProperty(assetAddress, 'reclaimFee', enabled)
  return property?.uintValue ?? 0n
}

export function useGetLatePenalty(assetAddress: `0x${string}` | undefined, enabled: boolean = true): bigint {
  const property = useGetProperty(assetAddress, 'lateRedemptionPenalty', enabled)
  return property?.uintValue ?? 0n
}

export function useGetTrinityMasterServiceFee(assetAddress: `0x${string}` | undefined, enabled: boolean = true): bigint {
  const property = useGetProperty(assetAddress, 'trinityMasterServiceFee', enabled)
  return property?.uintValue ?? 0n
}

export function useGetImage(assetAddress: `0x${string}` | undefined, enabled: boolean = true): string | undefined {
  // Get the properties contract address
  const { data: propertiesAddress } = useContractRead({
    address: assetAddress,
    abi: CONFIG.TRINITY_PRODUCT_ABI.abi as Abi,
    functionName: 'properties',
    enabled: enabled && !!assetAddress,
    watch: true,
  })

  // Use getUIImage to get the image
  const { data: imageData } = useContractRead({
    address: propertiesAddress as `0x${string}`,
    abi: CONFIG.TRINITY_PROPERTIES_ABI.abi as Abi,
    functionName: 'getUIImage',
    args: [0], // Get the first image (index 0)
    enabled: enabled && !!assetAddress && !!propertiesAddress,
    watch: true,
  }) as { data: string }

  return imageData
} 
